import React, { useCallback, useEffect, useState } from "react";
import "./analytics.css";
import { getCategoryAnalytics, getCategoryCycles, getCategoryTopUsers, getCategoryWallet, errorMessage } from "./analyticsApi";
import { Kpi, Loading, Pager, Progress, RangeBar, RevenueChart, UserCell } from "./parts";
import { CYCLE_STATUS, REASON_LABELS, RUN_STATUS, compact, dateOnly, dateTime, duration, fmt } from "./format";

/// The category's own numbers: its goal cycle (how far to the goal, what is
/// held pending, time left), what it earned in any date range, which users
/// brought it, its past cycles and its balance history.
export default function Analytics({ onBalance }) {
  const [range, setRange] = useState({ from: "", to: "", preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("users");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await getCategoryAnalytics({ from: range.from, to: range.to });
      setData(r);
      if (onBalance && r?.category) onBalance(r.category.coins);
    } catch (e) {
      setError(errorMessage(e, "Could not load analytics"));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, onBalance]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;
  const c = data?.current_cycle;
  const runStatus = data?.run?.run_status || "stopped";
  const paused = runStatus === "paused";
  const st = c ? (paused ? RUN_STATUS.paused : CYCLE_STATUS[c.status]) : null;

  return (
    <div className="rv-wrap">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h2 className="rv-title">Analytics</h2>
          <p className="rv-sub">
            While your category is running, you get 10% of every gift your users receive right away, plus 20% held
            until you reach this month's goal.
          </p>
        </div>
        <RangeBar value={range} onChange={setRange} />
      </div>

      {error && <div className="rv-error mb-3">{error}</div>}
      {loading && !data ? (
        <Loading />
      ) : data ? (
        <>
          <div className="rv-panel mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="rv-panel-title mb-0">{c ? `Goal cycle #${c.cycle_no}` : "Goal cycle"}</div>
              {st && <span className={`rv-badge ${st.cls}`}>{st.label}</span>}
            </div>
            {c ? (
              <>
                <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-2">
                  <div>
                    <div className="rv-kpi-value mb-0">{fmt(c.achieved_coins)}</div>
                    <div className="text-grey small">of {fmt(c.goal_coins)} coins goal</div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold fs-3">{c.progress}%</div>
                    <div className="text-grey small">
                      {paused ? "Paused · " : ""}
                      {duration(c.seconds_left)} left · ends {dateOnly(c.ends_at)}
                    </div>
                  </div>
                </div>
                <Progress value={c.progress} />
                <div className="row mt-3 g-3">
                  <div className="col-md-4">
                    <div className="rv-kpi gold h-100">
                      <div className="rv-kpi-label">Remaining to goal</div>
                      <div className="rv-kpi-value">{compact(c.remaining_coins)}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="rv-kpi grey h-100">
                      <div className="rv-kpi-label">Pending (waiting for the goal)</div>
                      <div className="rv-kpi-value">{compact(c.pending_coins)}</div>
                      <div className="rv-kpi-foot">
                        {c.status === "running"
                          ? `Paid to you the moment you reach ${compact(c.goal_coins)}. Not reached by ${dateOnly(c.ends_at)} (or the category is stopped) → it goes back to the app.`
                          : "Goal reached — the 20% is now paid to you straight away."}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="rv-kpi green h-100">
                      <div className="rv-kpi-label">Released to you this cycle</div>
                      <div className="rv-kpi-value">{compact(c.released_coins)}</div>
                      <div className="rv-kpi-foot">Started {dateOnly(c.starts_at)}</div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="rv-empty">
                Your category is stopped, so nothing is counted right now. A new cycle starts from zero when the admin
                starts it.
              </div>
            )}
          </div>

          <div className="rv-kpis mb-3">
            <Kpi
              tone="green"
              label="You earned"
              value={compact(s.earned)}
              title={fmt(s.earned)}
              foot={
                <>
                  Direct <b>{fmt(s.direct)}</b> · released <b>{fmt(s.released)}</b>
                </>
              }
            />
            <Kpi
              tone="gold"
              label="Coins generated"
              value={compact(s.net_generated)}
              title={fmt(s.net_generated)}
              foot={
                <>
                  Counts toward the goal · <b>{fmt(s.gifts_count)}</b> gifts
                </>
              }
            />
            <Kpi
              tone="grey"
              label="Pending created"
              value={compact(s.pending_created)}
              foot={
                <>
                  Held now <b>{fmt(s.pending_held_now)}</b> · lost <b>{fmt(s.pending_forfeited)}</b>
                </>
              }
            />
            <Kpi
              tone="pink"
              label="Your users"
              value={fmt(s.users)}
              foot={
                <>
                  Earned <b>{fmt(s.hosts_diamonds)}</b> diamonds · <b>{fmt(s.senders)}</b> senders
                </>
              }
            />
          </div>

          <div className="rv-panel mb-3">
            <div className="rv-panel-title">
              Trend <span className="text-grey fs-6">({data.timeseries.bucket === "day" ? "per day" : "per month"})</span>
            </div>
            <RevenueChart points={data.timeseries.points} keys={["earned"]} />
          </div>
        </>
      ) : null}

      <div className="rv-panel">
        <div className="rv-tabs mb-3">
          {[
            ["users", "Top users"],
            ["cycles", "Cycle history"],
            ["wallet", "Balance history"],
          ].map(([k, l]) => (
            <button key={k} className={`rv-tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
              {l}
            </button>
          ))}
        </div>
        {tab === "users" && <TopUsers range={range} />}
        {tab === "cycles" && <Cycles />}
        {tab === "wallet" && <WalletHistory />}
      </div>
    </div>
  );
}

function TopUsers({ range }) {
  const [sort, setSort] = useState("category_generated");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => setPage(1), [range.from, range.to, sort]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getCategoryTopUsers({ from: range.from, to: range.to, sort, page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch(() => alive && setRes({ data: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range.from, range.to, sort, page]);

  return (
    <>
      <select className="rv-select mb-3" style={{ maxWidth: 260 }} value={sort} onChange={(e) => setSort(e.target.value)}>
        <option value="category_generated">Sort: earned for you</option>
        <option value="net">Sort: coins generated</option>
        <option value="gifts_count">Sort: gifts</option>
        <option value="diamonds">Sort: their diamonds</option>
      </select>
      {loading && !res ? (
        <Loading />
      ) : !res?.data?.length ? (
        <div className="rv-empty">No gifts in this range yet.</div>
      ) : (
        <>
          <div className="rv-table-wrap">
            <table className="rv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th className="rv-num">Gifts</th>
                  <th className="rv-num">Senders</th>
                  <th className="rv-num">Coins generated</th>
                  <th className="rv-num">For you</th>
                  <th className="rv-num">Their diamonds</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {res.data.map((r) => (
                  <tr key={r.user.id}>
                    <td className="text-grey">{r.rank}</td>
                    <td>
                      <UserCell user={r.user} />
                    </td>
                    <td className="rv-num">{fmt(r.gifts_count)}</td>
                    <td className="rv-num">{fmt(r.senders)}</td>
                    <td className="rv-num">{fmt(r.net)}</td>
                    <td className="rv-num fw-bold">{fmt(r.category_generated)}</td>
                    <td className="rv-num">{fmt(r.diamonds)}</td>
                    <td>
                      {r.target ? (
                        <span className={`rv-badge ${r.target.status === "complete" ? "rv-badge-green" : "rv-badge-violet"}`}>
                          {r.target.status === "complete" ? "Completed" : "Active"} · {dateOnly(r.target.end_time)}
                        </span>
                      ) : (
                        <span className="rv-badge rv-badge-grey">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager meta={res.meta} onPage={setPage} />
        </>
      )}
    </>
  );
}

function Cycles() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    getCategoryCycles()
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <Loading />;
  if (!rows.length) return <div className="rv-empty">No cycles yet.</div>;
  return (
    <div className="rv-table-wrap">
      <table className="rv-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Period</th>
            <th className="rv-num">Achieved / goal</th>
            <th style={{ minWidth: 160 }}>Progress</th>
            <th className="rv-num">Released to you</th>
            <th className="rv-num">Went to app</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.id}>
              <td>{x.cycle_no}</td>
              <td className="small text-grey">
                {dateOnly(x.starts_at)} → {dateOnly(x.ends_at)}
              </td>
              <td className="rv-num">
                {fmt(x.achieved_coins)} / {fmt(x.goal_coins)}
              </td>
              <td>
                <Progress value={x.progress} size="sm" />
              </td>
              <td className="rv-num">{fmt(x.released_coins)}</td>
              <td className="rv-num">{fmt(x.forfeited_coins)}</td>
              <td>
                <span className={`rv-badge ${CYCLE_STATUS[x.status]?.cls}`}>{CYCLE_STATUS[x.status]?.label}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WalletHistory() {
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);
  useEffect(() => {
    let alive = true;
    getCategoryWallet({ page, limit: 20 })
      .then((r) => alive && setRes(r))
      .catch(() => alive && setRes({ data: [] }));
    return () => {
      alive = false;
    };
  }, [page]);

  if (!res) return <Loading />;
  if (!res.data?.length) return <div className="rv-empty">No balance changes yet.</div>;
  return (
    <>
      <div className="rv-table-wrap">
        <table className="rv-table" style={{ minWidth: 600 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>What</th>
              <th className="rv-num">Amount</th>
              <th className="rv-num">Balance after</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {res.data.map((t) => (
              <tr key={t.id}>
                <td className="small text-grey" style={{ whiteSpace: "nowrap" }}>
                  {dateTime(t.created_at)}
                </td>
                <td>{REASON_LABELS[t.reason] || t.reason}</td>
                <td className={`rv-num ${t.amount >= 0 ? "rv-amount-pos" : "rv-amount-neg"}`}>
                  {t.amount >= 0 ? "+" : ""}
                  {fmt(t.amount)}
                </td>
                <td className="rv-num">{t.balance_after !== null ? fmt(t.balance_after) : "—"}</td>
                <td className="small text-grey">{t.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager meta={res.meta} onPage={setPage} />
    </>
  );
}
